import {
  onBeforeUnmount,
  onMounted,
  ref,
  useContext,
  useRoute,
} from '@nuxtjs/composition-api'
import {
  getSupportMessages,
  sendSupportMessage,
  sendSupportMessageWithAttachments,
} from '@/api/support'
import logger from '@/utils/logger'

export const SUPPORT_REPLY_EVENT = 'support:reply'

export function useSupportChat() {
  const { i18n } = useContext()
  const route = useRoute()
  const orgId = route.value.params.orgId
  const t = (key) => i18n.t(`pages.support.${key}`)

  const messages = ref([])
  const loading = ref(true)
  const loadingOlder = ref(false)
  const hasOlder = ref(true)
  const sending = ref(false)
  const error = ref('')

  // Ответы приходят и по сокету, и из refetch: склеиваем по id, чтобы не плодить дубли
  function merge(items) {
    const byId = new Map(messages.value.map((message) => [message.id, message]))

    for (const item of items) byId.set(item.id, item)
    messages.value = [...byId.values()].sort((a, b) => a.id - b.id)
  }

  async function refresh() {
    try {
      const { data } = await getSupportMessages(orgId)

      merge(data.items)
      error.value = ''
    } catch (err) {
      logger.error('Ошибка загрузки переписки с поддержкой', err)
      error.value = t('load_error')
    } finally {
      loading.value = false
    }
  }

  async function loadOlder() {
    if (loadingOlder.value || !messages.value.length) return

    loadingOlder.value = true
    try {
      const { data } = await getSupportMessages(orgId, {
        before: messages.value[0].id,
      })
      if (!data.items.length) hasOlder.value = false
      merge(data.items)
    } catch (err) {
      logger.error('Ошибка загрузки ранних сообщений поддержки', err)
      error.value = t('load_error')
    } finally {
      loadingOlder.value = false
    }
  }

  function buildForm(text, files) {
    const form = new FormData()

    if (text) form.append('content', text)
    for (const file of files) form.append('attachments', file, file.name)

    return form
  }

  async function send(content, files = []) {
    const text = content.trim()
    if ((!text && !files.length) || sending.value) return false

    sending.value = true
    error.value = ''
    try {
      const { data } = files.length
        ? await sendSupportMessageWithAttachments(orgId, buildForm(text, files))
        : await sendSupportMessage(orgId, { content: text })
      merge([data.item])
      return true
    } catch (err) {
      logger.error('Ошибка отправки сообщения в поддержку', err)
      error.value = err?.response?.data?.message || t('send_error')
      return false
    } finally {
      sending.value = false
    }
  }

  function onSupportReply(payload) {
    if (String(payload?.orgId) === String(orgId)) refresh()
  }

  // Ответ, пришедший пока вкладка спала или сокет переподключался, подтягиваем при возврате
  function onVisibilityChange() {
    if (document.visibilityState === 'visible') refresh()
  }

  onMounted(() => {
    refresh()

    window.$nuxt?.$on(SUPPORT_REPLY_EVENT, onSupportReply)
    document.addEventListener('visibilitychange', onVisibilityChange)
  })

  onBeforeUnmount(() => {
    window.$nuxt?.$off(SUPPORT_REPLY_EVENT, onSupportReply)
    document.removeEventListener('visibilitychange', onVisibilityChange)
  })

  return {
    messages,
    loading,
    loadingOlder,
    hasOlder,
    sending,
    error,
    loadOlder,
    send,
  }
}
